/* AppErrorBoundary + LoadingGate — záchranná síť pro „katastrofu" při pádu/načítání.
 *
 * ErrorBoundary: jakýkoli pád v renderu → místo bílé obrazovky přívětivá hláška
 *   s tlačítkem Obnovit + Zkusit znovu (reset bez reloadu) + diagnostický kód.
 * LoadingGate: místo holého spinneru zpráva, a když načítání trvá moc dlouho,
 *   nabídne „Zkusit znovu" / „Přihlásit" (žádné věčné točení).
 */
import { Component, useEffect, useState } from "react";
import { AlertTriangle, RefreshCw, LogIn } from "lucide-react";

/* PĚT JAZYKŮ I V KATASTROFĚ — a schválně BEZ `useLanguage` a BEZ slovníků.
 * ---------------------------------------------------------------------------
 * NÁLEZ BRÁNY (25. 8. 2026): vietnamský poradce viděl při pádu appky českou
 * hlášku „Něco se nepovedlo". Tahle obrazovka je poslední, co zbyde, když
 * spadne všechno ostatní — takže se nesmí opřít o nic, co může spadnout taky:
 *   • `useLanguage()` je hook a tohle je class component (ErrorBoundary jím
 *     být MUSÍ — funkční komponenta chybu potomka nezachytí),
 *   • kontext jazyka nemusí existovat, když padl právě `LanguageProvider`,
 *   • `t()` táhne slovníky (stovky kB) do cesty, která má být neprůstřelná.
 * Proto malá vlastní tabulka a jazyk přímo z localStorage v try/catch.
 * Klíč `bp_lang` je týž, jaký zapisuje LanguageContext. */
const HLASKY = {
  cs: { titul: "Něco se nepovedlo", popis: "Stránku se nepodařilo zobrazit. Zkus to prosím znovu — tvoje data jsou v pořádku.", obnovit: "Obnovit aplikaci", znovu: "Zkusit znovu bez obnovení", kod: "Kód", nacitam: "Načítám…", pomalu: "Trvá to déle než obvykle — server se možná probouzí.", zkusit: "Zkusit znovu", prihlasit: "Přihlásit" },
  sk: { titul: "Niečo sa nepodarilo", popis: "Stránku sa nepodarilo zobraziť. Skús to prosím znova — tvoje dáta sú v poriadku.", obnovit: "Obnoviť aplikáciu", znovu: "Skúsiť znova bez obnovenia", kod: "Kód", nacitam: "Načítavam…", pomalu: "Trvá to dlhšie než zvyčajne — server sa možno prebúdza.", zkusit: "Skúsiť znova", prihlasit: "Prihlásiť" },
  en: { titul: "Something went wrong", popis: "The page could not be displayed. Please try again — your data is safe.", obnovit: "Reload the app", znovu: "Try again without reloading", kod: "Code", nacitam: "Loading…", pomalu: "This is taking longer than usual — the server may be waking up.", zkusit: "Try again", prihlasit: "Sign in" },
  uk: { titul: "Щось пішло не так", popis: "Не вдалося показати сторінку. Спробуйте ще раз — ваші дані в безпеці.", obnovit: "Перезавантажити застосунок", znovu: "Спробувати без перезавантаження", kod: "Код", nacitam: "Завантаження…", pomalu: "Це триває довше, ніж зазвичай — сервер, можливо, прокидається.", zkusit: "Спробувати ще раз", prihlasit: "Увійти" },
  vi: { titul: "Đã xảy ra lỗi", popis: "Không thể hiển thị trang. Vui lòng thử lại — dữ liệu của bạn vẫn an toàn.", obnovit: "Tải lại ứng dụng", znovu: "Thử lại mà không tải lại", kod: "Mã", nacitam: "Đang tải…", pomalu: "Việc này lâu hơn bình thường — máy chủ có thể đang khởi động.", zkusit: "Thử lại", prihlasit: "Đăng nhập" },
};

/** Jazyk z localStorage. Cokoli se pokazí (privátní režim, cizí hodnota) → čeština. */
export function hlaskyKatastrofy() {
  try {
    const l = window.localStorage.getItem("bp_lang");
    return HLASKY[l] || HLASKY.cs;
  } catch {
    return HLASKY.cs;
  }
}

export class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, code: "" };
  }
  static getDerivedStateFromError(error) {
    const code = (error?.message || "ERR").replace(/[^a-zA-Z0-9]/g, "").slice(0, 8).toUpperCase() || "ERR";
    return { hasError: true, code };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary]", error, info?.componentStack);
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    const H = hlaskyKatastrofy();
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#FAF7F3" }}>
        <div className="max-w-md w-full text-center rounded-2xl p-8" style={{ background: "#fff", border: "1px solid #E8DCC8", boxShadow: "0 10px 40px rgba(0,0,0,0.08)" }}>
          <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4" style={{ background: "rgba(194,89,52,0.12)", color: "#C25934" }}>
            <AlertTriangle size={26} />
          </div>
          <h1 className="text-[19px] font-bold mb-1" style={{ color: "#1A1A1A" }}>{H.titul}</h1>
          <p className="text-[13.5px] mb-5" style={{ color: "#8C8378" }}>
            {H.popis}
          </p>
          <div className="flex flex-col gap-2">
            <button onClick={() => window.location.reload()} className="inline-flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-[14px] text-white" style={{ background: "#C25934" }}>
              <RefreshCw size={15} /> {H.obnovit}
            </button>
            <button onClick={() => this.setState({ hasError: false })} className="inline-flex items-center justify-center gap-2 py-2 rounded-xl font-semibold text-[13px]" style={{ background: "#F4ECE0", color: "#5C5249" }}>
              {H.znovu}
            </button>
          </div>
          <div className="text-[11px] mt-4" style={{ color: "#B8AE9E" }}>{H.kod}: {this.state.code}</div>
        </div>
      </div>
    );
  }
}

/* Spinner + zpráva; po `slowAfter` ms nabídne akce, ať to netočí donekonečna. */
export function LoadingGate({ message, slowAfter = 8000 }) {
  const H = hlaskyKatastrofy();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), slowAfter);
    return () => clearTimeout(t);
  }, [slowAfter]);
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#FAF7F3" }}>
      <div className="text-center">
        <div className="w-8 h-8 mx-auto border-2 rounded-full animate-spin" style={{ borderColor: "#C25934", borderTopColor: "transparent" }} />
        <div className="text-[13px] mt-4 font-semibold" style={{ color: "#5C5249" }}>{message || H.nacitam}</div>
        {slow && (
          <div className="mt-5">
            <div className="text-[12px] mb-2" style={{ color: "#8C8378" }}>{H.pomalu}</div>
            <div className="flex items-center justify-center gap-2">
              <button onClick={() => window.location.reload()} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl font-bold text-[13px] text-white" style={{ background: "#C25934" }}>
                <RefreshCw size={14} /> {H.zkusit}
              </button>
              <a href="/login" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl font-semibold text-[13px]" style={{ background: "#F4ECE0", color: "#5C5249" }}>
                <LogIn size={14} /> {H.prihlasit}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
